import { coerceMoneyFromApi } from '@shared/utils/money';
import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Observable, map } from 'rxjs';
import { API_BASE_URL } from '@core/config/api.config';
import { ApiEndpoints } from '@core/config/api-endpoints';
import { GRAPHQL_MEDIA_URLS_FIELDS } from '@core/media/graphql/media-urls.fields';
import { MediaAssetUrls } from '@core/media/models/media-urls.models';
import { mapMediaUrlsFromDto } from '@core/media/utils/media-urls.mapper';
import {
  CatalogPromotion,
  CatalogPromotionInput,
  CatalogPromotionMutationResult,
  CatalogSectionSetting,
  CatalogSectionSettingInput,
  CatalogSectionSettingMutationResult,
} from '../models/catalog-marketing.models';
import { GraphqlProductDto } from './dto/product.dto';
import { mapProductDto } from './mappers/product.mapper';
import {
  ensureNoTopLevelErrors,
  GraphqlHttpResponse,
  mapEntityMutationResult,
  mapLifecycleMutationResult,
} from './mappers/graphql-error.mapper';

/** Nested product payload for promotions list/form — keep lean (no categories/attributes). */
const PROMOTION_PRODUCT_FIELDS = `
  id
  name
  price
  active
  imageUrl
  ${GRAPHQL_MEDIA_URLS_FIELDS}
`;

const PROMOTION_FIELDS = `
  id
  kind
  productId
  name
  description
  active
  startsAt
  endsAt
  discountType
  discountValue
  promotionalPrice
  minOrderAmount
  badgeText
  showInShowcase
  imageUrl
  ${GRAPHQL_MEDIA_URLS_FIELDS}
  cardStyle
  backgroundImageUrl
  backgroundImageUrls {
    original
    thumb
    medium
    large
  }
  position
  visibleNow
  originalPrice
  resolvedPrice
  product {
    ${PROMOTION_PRODUCT_FIELDS}
  }
`;

const SETTINGS_QUERY = `
  query CatalogSectionSettings {
    catalogSectionSettings {
      id
      sectionKey
      enabled
      title
      limit
      position
      selectionMode
      periodDays
      metric
    }
  }
`;

const PROMOTIONS_QUERY = `
  query CatalogPromotions($activeOnly: Boolean) {
    catalogPromotions(activeOnly: $activeOnly) {
      ${PROMOTION_FIELDS}
    }
  }
`;

const UPDATE_SETTING_MUTATION = `
  mutation UpdateCatalogSectionSetting($input: CatalogSectionSettingInput!) {
    updateCatalogSectionSetting(input: $input) {
      setting {
        id
        sectionKey
        enabled
        title
        limit
        position
        selectionMode
        periodDays
        metric
      }
      errors
    }
  }
`;

const CREATE_PROMOTION_MUTATION = `
  mutation CreateCatalogPromotion($input: CatalogPromotionInput!) {
    createCatalogPromotion(input: $input) {
      promotion {
        ${PROMOTION_FIELDS}
      }
      errors
    }
  }
`;

const UPDATE_PROMOTION_MUTATION = `
  mutation UpdateCatalogPromotion($id: ID!, $input: CatalogPromotionInput!) {
    updateCatalogPromotion(id: $id, input: $input) {
      promotion {
        ${PROMOTION_FIELDS}
      }
      errors
    }
  }
`;

const DESTROY_PROMOTION_MUTATION = `
  mutation DestroyCatalogPromotion($id: ID!) {
    destroyCatalogPromotion(id: $id) {
      promotion {
        ${PROMOTION_FIELDS}
      }
      errors
    }
  }
`;

const PERMANENTLY_DELETE_PROMOTION_MUTATION = `
  mutation PermanentlyDeleteCatalogPromotion($id: ID!) {
    permanentlyDeleteCatalogPromotion(id: $id) {
      success
      errors
    }
  }
`;

interface CatalogSectionSettingDto {
  id?: string | number | null;
  sectionKey: string;
  enabled: boolean;
  title: string;
  limit: number;
  position: number;
  selectionMode: string;
  periodDays: number;
  metric: string;
}

interface CatalogPromotionDto {
  id: string | number;
  kind?: string | null;
  productId?: string | number | null;
  product?: GraphqlProductDto | null;
  name: string;
  description?: string | null;
  active: boolean;
  startsAt?: string | null;
  endsAt?: string | null;
  discountType: string;
  discountValue?: number | null;
  promotionalPrice?: number | null;
  minOrderAmount?: number | null;
  badgeText?: string | null;
  showInShowcase?: boolean | null;
  imageUrl?: string | null;
  imageUrls?: MediaAssetUrls | null;
  cardStyle?: string | null;
  backgroundImageUrl?: string | null;
  backgroundImageUrls?: MediaAssetUrls | null;
  position: number;
  visibleNow: boolean;
  originalPrice: number;
  resolvedPrice: number;
}

interface MutationSettingPayloadDto {
  setting?: CatalogSectionSettingDto | null;
  errors?: unknown;
}

interface MutationPromotionPayloadDto {
  promotion?: CatalogPromotionDto | null;
  errors?: unknown;
}

function normalizeErrors(errors: unknown): string[] {
  if (!Array.isArray(errors)) {
    return [];
  }
  return errors.map((error) => String(error)).filter(Boolean);
}

function mapSetting(dto: CatalogSectionSettingDto): CatalogSectionSetting {
  return {
    id: dto.id == null ? null : String(dto.id),
    sectionKey: dto.sectionKey === 'promotions' ? 'promotions' : 'best_sellers',
    enabled: dto.enabled,
    title: dto.title,
    limit: dto.limit,
    position: dto.position,
    selectionMode:
      dto.selectionMode === 'manual' || dto.selectionMode === 'automatic'
        ? dto.selectionMode
        : 'hybrid',
    periodDays: dto.periodDays,
    metric: dto.metric === 'revenue' ? 'revenue' : 'quantity',
  };
}

function mapPromotion(dto: CatalogPromotionDto): CatalogPromotion {
  return {
    id: String(dto.id),
    kind: dto.kind === 'free_delivery' ? 'free_delivery' : 'product_discount',
    productId: dto.productId == null || dto.productId === '' ? null : String(dto.productId),
    product: dto.product ? mapProductDto(dto.product) : null,
    name: dto.name,
    description: dto.description ?? null,
    active: dto.active,
    startsAt: dto.startsAt ?? null,
    endsAt: dto.endsAt ?? null,
    discountType:
      dto.discountType === 'percent' || dto.discountType === 'fixed_amount'
        ? dto.discountType
        : 'special_price',
    discountValue: dto.discountValue ?? null,
    promotionalPrice:
      dto.promotionalPrice == null ? null : (coerceMoneyFromApi(dto.promotionalPrice) ?? null),
    minOrderAmount: dto.minOrderAmount == null ? null : coerceMoneyFromApi(dto.minOrderAmount),
    badgeText: dto.badgeText?.trim() || null,
    showInShowcase: dto.showInShowcase !== false,
    imageUrl: dto.imageUrl?.trim() || null,
    imageUrls: mapMediaUrlsFromDto(dto.imageUrls),
    cardStyle: normalizeCardStyle(dto.cardStyle),
    backgroundImageUrl: dto.backgroundImageUrl?.trim() || null,
    backgroundImageUrls: mapMediaUrlsFromDto(dto.backgroundImageUrls),
    position: dto.position,
    visibleNow: dto.visibleNow,
    originalPrice: coerceMoneyFromApi(dto.originalPrice) ?? 0,
    resolvedPrice: coerceMoneyFromApi(dto.resolvedPrice) ?? 0,
  };
}

function normalizeCardStyle(value?: string | null): CatalogPromotion['cardStyle'] {
  switch (value) {
    case 'soft_coral':
    case 'soft_mint':
    case 'soft_slate':
    case 'plain':
    case 'image':
      return value;
    default:
      return 'soft_rose';
  }
}

@Injectable()
export class CatalogMarketingApiService {
  constructor(
    private readonly http: HttpClient,
    @Inject(API_BASE_URL) private readonly baseUrl: string
  ) {}

  settings(): Observable<CatalogSectionSetting[]> {
    return this.graphql<{ catalogSectionSettings: CatalogSectionSettingDto[] }>(
      SETTINGS_QUERY,
      {},
      'CatalogSectionSettings'
    ).pipe(map((data) => data.catalogSectionSettings.map(mapSetting)));
  }

  promotions(activeOnly: boolean | null = null): Observable<CatalogPromotion[]> {
    return this.graphql<{ catalogPromotions: CatalogPromotionDto[] }>(
      PROMOTIONS_QUERY,
      { activeOnly },
      'CatalogPromotions'
    ).pipe(map((data) => data.catalogPromotions.map(mapPromotion)));
  }

  updateSetting(input: CatalogSectionSettingInput): Observable<CatalogSectionSettingMutationResult> {
    return this.graphql<{ updateCatalogSectionSetting: MutationSettingPayloadDto }>(
      UPDATE_SETTING_MUTATION,
      { input },
      'UpdateCatalogSectionSetting'
    ).pipe(
      map((data) => ({
        setting: data.updateCatalogSectionSetting.setting
          ? mapSetting(data.updateCatalogSectionSetting.setting)
          : null,
        errors: normalizeErrors(data.updateCatalogSectionSetting.errors),
      }))
    );
  }

  createPromotion(input: CatalogPromotionInput): Observable<CatalogPromotionMutationResult> {
    return this.graphql<{ createCatalogPromotion: MutationPromotionPayloadDto }>(
      CREATE_PROMOTION_MUTATION,
      { input },
      'CreateCatalogPromotion'
    ).pipe(map((data) => this.mapPromotionPayload(data.createCatalogPromotion)));
  }

  updatePromotion(
    id: string,
    input: CatalogPromotionInput
  ): Observable<CatalogPromotionMutationResult> {
    return this.graphql<{ updateCatalogPromotion: MutationPromotionPayloadDto }>(
      UPDATE_PROMOTION_MUTATION,
      { id, input },
      'UpdateCatalogPromotion'
    ).pipe(map((data) => this.mapPromotionPayload(data.updateCatalogPromotion)));
  }

  destroyPromotion(id: string): Observable<CatalogPromotionMutationResult> {
    return this.graphqlMutation<{ destroyCatalogPromotion: MutationPromotionPayloadDto }>(
      DESTROY_PROMOTION_MUTATION,
      { id },
      'DestroyCatalogPromotion'
    ).pipe(
      map((res) =>
        mapEntityMutationResult(res, 'destroyCatalogPromotion', (payload) =>
          this.mapPromotionPayload(payload as MutationPromotionPayloadDto)
        )
      )
    );
  }

  updatePromotionActive(
    id: string,
    active: boolean
  ): Observable<CatalogPromotionMutationResult> {
    return this.graphql<{ updateCatalogPromotion: MutationPromotionPayloadDto }>(
      UPDATE_PROMOTION_MUTATION,
      { id, input: { active } },
      'UpdateCatalogPromotion'
    ).pipe(map((data) => this.mapPromotionPayload(data.updateCatalogPromotion)));
  }

  permanentlyDeletePromotion(id: string): Observable<{ success: boolean; errors: string[] }> {
    return this.graphqlMutation<{
      permanentlyDeleteCatalogPromotion: { success?: boolean; errors?: string[] };
    }>(PERMANENTLY_DELETE_PROMOTION_MUTATION, { id }, 'PermanentlyDeleteCatalogPromotion').pipe(
      map((res) => mapLifecycleMutationResult(res, 'permanentlyDeleteCatalogPromotion'))
    );
  }

  private mapPromotionPayload(payload: MutationPromotionPayloadDto): CatalogPromotionMutationResult {
    return {
      promotion: payload.promotion ? mapPromotion(payload.promotion) : null,
      errors: normalizeErrors(payload.errors),
    };
  }

  private graphql<TData>(
    query: string,
    variables: Record<string, unknown>,
    operationName: string
  ): Observable<TData> {
    return this.graphqlMutation<TData>(query, variables, operationName).pipe(
      map((response) => ensureNoTopLevelErrors(response))
    );
  }

  private graphqlMutation<TData>(
    query: string,
    variables: Record<string, unknown>,
    operationName: string
  ): Observable<GraphqlHttpResponse<TData>> {
    return this.http.post<GraphqlHttpResponse<TData>>(`${this.baseUrl}${ApiEndpoints.graphql}`, {
      query,
      variables,
      operationName,
    });
  }
}
